import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { useDocumentTitle } from '../_hooks/useDocumentTitle';
import { Alert } from '../CommonComponents/OPL/Alert/Alert';
import { Lozenge } from '../CommonComponents/OPL/Lozenge/Lozenge';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { Dropzone } from '../CommonComponents/OPL/Dropzone/Dropzone';
import { Loading } from '../CommonComponents/OPL/Loading/Loading';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { addClient as adviserAreaAddClientAction } from '../AdviserArea/AdviserAreaActions';
import { queryKeys as clientSearchQKs } from '../ClientSearch/api/queryKeys';
import { toast } from '../CommonComponents/OPL/Toast/Toast';
import { BatchStatusResult, BatchItemResult, isBatchInProgress } from './api/pdfBatchTypes';
import { usePdfCreateBatchMutation } from './api/usePdfCreateBatchMutation';
import { usePdfAddFileMutation, AddFileResult } from './api/usePdfAddFileMutation';
import { usePdfProcessBatchMutation } from './api/usePdfProcessBatchMutation';
import { usePdfAllBatchesQuery, allBatchesQueryKey } from './api/usePdfAllBatchesQuery';
import { BatchTable } from './components/BatchTable';
import { makeEmptyItem, MAX_CONCURRENT_UPLOADS } from './PdfImportPage.meta';
import { Icon } from '../CommonComponents/OPL/Icon/Icon';

const INITIAL_BATCH_COUNT = 5;
const BATCH_PAGE_SIZE = 10;

export const PdfImportPage = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [visibleBatchCount, setVisibleBatchCount] = useState(INITIAL_BATCH_COUNT);

  const [uploadingBatch, setUploadingBatch] = useState<BatchStatusResult | null>(null);

  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const uploadAbortRef = useRef(false);

  const { mutateAsync: createBatch, error: createBatchError } = usePdfCreateBatchMutation();
  const { mutateAsync: addFile, error: addFileError } = usePdfAddFileMutation();
  const { mutateAsync: processBatch, error: processBatchError } = usePdfProcessBatchMutation();

  const { data: allBatches, isLoading: isBatchesLoading } = usePdfAllBatchesQuery();

  useDocumentTitle('Import Financial Plans');

  const displayBatches = useMemo(() => {
    const apiBatches = !isBatchesLoading && allBatches ? allBatches : [];

    if (!uploadingBatch) {
      return apiBatches;
    }

    const filtered = apiBatches.filter(b => b.batchId !== uploadingBatch.batchId);

    return [uploadingBatch, ...filtered];
  }, [uploadingBatch, allBatches, isBatchesLoading]);

  const hasActiveBatches = displayBatches.some(isBatchInProgress);

  const handleFileDrop = useCallback((acceptedFiles: File[]) => {
    setSelectedFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      const newFiles = acceptedFiles.filter(f => !existingNames.has(f.name));
      return [...prev, ...newFiles];
    });
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleImportAll = useCallback(async () => {
    if (selectedFiles.length === 0) {
      return;
    }

    const filesToUpload = [...selectedFiles];

    setIsImporting(true);
    setSelectedFiles([]);
    uploadAbortRef.current = false;

    const syntheticItems: BatchItemResult[] = filesToUpload.map(f => makeEmptyItem(f.name));

    try {
      const result = await createBatch();
      const currentBatchId = result.batchId;

      setUploadingBatch({
        batchId: currentBatchId,
        status: 'Uploading',
        createdAtUtc: new Date().toISOString(),
        completedAtUtc: null,
        totalFiles: filesToUpload.length,
        completedFiles: 0,
        succeededFiles: 0,
        failedFiles: 0,
        items: syntheticItems,
      });

      let nextIndex = 0;
      let uploadedCount = 0;

      const uploadNext = async (): Promise<void> => {
        while (nextIndex < filesToUpload.length) {
          if (uploadAbortRef.current) return;

          const idx = nextIndex++;

          syntheticItems[idx] = { ...syntheticItems[idx], status: 'Uploading' };
          setUploadingBatch(prev => (prev ? { ...prev, items: [...syntheticItems] } : null));

          try {
            const addResult: AddFileResult = await addFile({
              batchId: currentBatchId,
              file: filesToUpload[idx],
            });

            syntheticItems[idx] = {
              ...syntheticItems[idx],
              status: addResult.isValid ? 'Uploaded' : 'Skipped',
              errors: addResult.errors ?? [],
            };
          } catch {
            syntheticItems[idx] = {
              ...syntheticItems[idx],
              status: 'Skipped',
              errors: ['Upload failed due to a network error.'],
            };
          }

          uploadedCount++;
          setUploadingBatch(prev =>
            prev
              ? {
                  ...prev,
                  completedFiles: uploadedCount,
                  items: [...syntheticItems],
                }
              : null,
          );
        }
      };

      const workers = Array.from(
        { length: Math.min(MAX_CONCURRENT_UPLOADS, filesToUpload.length) },
        () => uploadNext(),
      );
      await Promise.all(workers);

      if (uploadAbortRef.current) {
        setUploadingBatch(null);
        setIsImporting(false);
        return;
      }

      const hasValidFiles = syntheticItems.some(i => i.status === 'Uploaded');

      await processBatch({ batchId: currentBatchId });

      if (hasValidFiles) {
        toast.success('Import started. You will receive a notification once it completes.');
        ADV.util.clearDashboardCache();
        dispatch(adviserAreaAddClientAction());
        queryClient.invalidateQueries(clientSearchQKs.clientList());
      } else {
        toast.error('No valid files to process. Check the details below.');
      }

      await queryClient.refetchQueries(allBatchesQueryKey);

      setUploadingBatch(null);
      setIsImporting(false);
    } catch {
      setUploadingBatch(null);
      setIsImporting(false);
    }
  }, [selectedFiles, createBatch, addFile, processBatch, queryClient, dispatch]);

  return (
    <>
      <Box mb="24">
        <Box display="flex" alignItems="center" css={({ theme }) => ({ gap: theme.space[12] })}>
          <Text as="h1" fontSize="h3" fontWeight={600}>
            Import Financial Plans
          </Text>
          <Lozenge variantColor="new">Beta</Lozenge>
        </Box>

        <Text color="neutral.600" mt="8">
          Upload PDF financial plans to automatically extract client data including accounts, goals,
          income, expenses, and more. A new household will be created for each uploaded document.
        </Text>
      </Box>

      <Alert
        variant="neutral"
        icon="far fa-lightbulb-on"
        message="This process uses AI to extract planning information and build a household profile. As a best practice, add a review step to ensure best results."
        mb="24"
      />

      {/* Upload Section */}
      <Box mb="32">
        {(createBatchError || addFileError || processBatchError) && (
          <Alert
            variant="error"
            message={
              createBatchError?.message || addFileError?.message || processBatchError?.message
            }
            mb="16"
          />
        )}

        <Dropzone
          options={{
            onDropAccepted: handleFileDrop,
            accept: ['.pdf', 'application/pdf'],
          }}
        />

        {selectedFiles.length > 0 && (
          <Box mt="16" css={{ maxHeight: 240, overflowY: 'auto' }}>
            {selectedFiles.map((file, index) => (
              <Box
                key={`${file.name}-${index}`}
                display="flex"
                alignItems="center"
                p="12"
                mb="8"
                borderRadius="medium"
                css={({ theme }) => ({
                  border: `1px solid ${theme.colors.neutral['200']}`,
                  gap: theme.space[12],
                })}
              >
                <Text fontSize="h4" color="primary.500">
                  <Icon className="far fa-file-pdf" />
                </Text>

                <Box>
                  <Text fontWeight={500} isTruncated>
                    {file.name}
                  </Text>
                  <Text fontSize="small" color="neutral.500">
                    {(file.size / (1024 * 1024)).toFixed(1)} MB
                  </Text>
                </Box>

                <Button variant="subtleInactive" ml="auto" onClick={() => handleRemoveFile(index)}>
                  Remove
                </Button>
              </Box>
            ))}
          </Box>
        )}

        <Box display="flex" alignItems="center" mt="16" css={{ gap: 12 }}>
          <Button
            onClick={handleImportAll}
            disabled={selectedFiles.length === 0 || isImporting}
            leftIcon="far fa-upload"
            data-testid="PlanImportCTA"
          >
            Import ({selectedFiles.length})
          </Button>
        </Box>
      </Box>

      {/* Batch History */}
      <Box>
        <Text as="h2" fontSize="h4" fontWeight={600} mb="16">
          Import History
        </Text>

        {hasActiveBatches && (
          <Alert
            variant="info"
            message="PDF extraction is running in the background. You can safely navigate away and continue using the app — you'll receive a notification when the process completes."
            mb="16"
          />
        )}

        {isBatchesLoading && !uploadingBatch && (
          <Box display="flex" justifyContent="center" py="48">
            <Loading size="medium" text="Loading import history..." />
          </Box>
        )}

        {!isBatchesLoading && displayBatches.length === 0 && (
          <Text color="neutral.500">No imports yet. Select PDF files above to get started.</Text>
        )}

        {displayBatches.slice(0, visibleBatchCount).map(batch => (
          <BatchTable key={batch.batchId} batch={batch} />
        ))}

        {displayBatches.length > visibleBatchCount && (
          <Box display="flex" justifyContent="center" mt="8">
            <Button
              variant="subtleInactive"
              onClick={() => setVisibleBatchCount(prev => prev + BATCH_PAGE_SIZE)}
              leftIcon="far fa-history"
            >
              Show older imports ({displayBatches.length - visibleBatchCount} more)
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};
